import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
export default function Border_Rounded() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">⭕ CSS Border Radius (Rounded Borders)</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <code>border-radius</code> property is used to round the corners of
        an element’s border. You can create slightly rounded corners,
        pill-shaped buttons, circles, or even custom shapes.
      </p>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock code={`selector { border-radius: value; }`} />
        <p>
          <strong>Values can be:</strong>
          <br />• Length (px, em, rem) → <code>border-radius: 10px;</code>
          <br />• Percentage (%) → <code>border-radius: 50%;</code>
        </p>
      </div>

      {/* Basic Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">Basic Examples</h2>
        <CodeCopyBlock
          code={`div { border: 2px solid #333; border-radius: 5px; }  /* small rounded */
div { border: 2px solid #333; border-radius: 15px; } /* medium rounded */
div { border: 2px solid #333; border-radius: 50%; }  /* circle */`}
        />

        <div
          style={{
            display: "flex",
            gap: "1rem",
            flexWrap: "wrap",
            marginTop: "1rem",
          }}
        >
          <div
            style={{
              border: "2px solid #333",
              borderRadius: "5px",
              padding: "20px",
            }}
          >
            5px
          </div>
          <div
            style={{
              border: "2px solid #333",
              borderRadius: "15px",
              padding: "20px",
            }}
          >
            15px
          </div>
          <div
            style={{
              border: "2px solid #333",
              borderRadius: "50%",
              width: "80px",
              height: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Circle
          </div>
        </div>
      </div>

      {/* Different Corner Values */}
      <div className="tutorial-section">
        <h2 className="section-title">Different Corner Values</h2>
        <p>
          You can specify up to <strong>4 values</strong> (top-left, top-right,
          bottom-right, bottom-left).
        </p>
        <CodeCopyBlock
          code={`div { border-radius: 10px 30px 50px 70px; } 
/* top-left | top-right | bottom-right | bottom-left */`}
        />
        <div
          style={{
            border: "2px solid #333",
            borderRadius: "10px 30px 50px 70px",
            padding: "20px",
            marginTop: "0.5rem",
          }}
        >
          Uneven corners
        </div>
      </div>

      {/* Pill Shapes */}
      <div className="tutorial-section">
        <h2 className="section-title">Pill-Shaped Buttons</h2>
        <p>
          By using a high <code>border-radius</code>, you can create pill-shaped
          elements (great for buttons).
        </p>
        <CodeCopyBlock code={`button { border-radius: 50px; }`} />
        <button
          style={{
            border: "2px solid #15803d",
            borderRadius: "50px",
            background: "#22c55e",
            color: "#fff",
            padding: "10px 20px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          ✅ Rounded Button
        </button>
      </div>

      {/* Elliptical Radius */}
      <div className="tutorial-section">
        <h2 className="section-title">Elliptical Border Radius</h2>
        <p>
          You can also use two values separated by <code>/</code> to create an
          elliptical shape.
        </p>
        <CodeCopyBlock code={`div { border-radius: 50% / 25%; }`} />
        <div
          style={{
            border: "2px solid #333",
            borderRadius: "50% / 25%",
            width: "150px",
            height: "100px",
            marginTop: "0.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Ellipse
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          After learning border radius, check out{" "}
          <a href="/css/box-shadow" className="learn-more">
            CSS Box Shadow
          </a>{" "}
          to add shadows and depth to your elements.
        </p>
      </div>
    </div>
  );
}
